import {
    Hash,
    P2PKH,
    PrivateKey,
    TransactionSignature,
    UnlockingScript,
    Utils,
} from '@treechat/bsv-sdk'
import type { MintContentSource } from './ordinal-local-mint-signing'

// Owner-paid "Write Onchain" signed on this device (self-custody wallets). The server prepares
// the B:// attachment record funded from this wallet (bsv/src/bsv/clientSignedOwnerAttachment.ts)
// and this module signs it only when it matches a Write this device just confirmed: every input
// is this wallet's, the only other output returns change here, and the miner fee stays under the
// amount authorized at the tap. The record itself carries no value; its bytes are re-read so the
// signature commits to exactly the transaction the server will broadcast.

const SIGHASH_ALL_FORKID = 0x41
const B_PROTOCOL_ADDRESS = '19HxigV4QyBv3tHpQVcUEQyq1pzZVdoAut'
const HEX_32 = /^[0-9a-f]{64}$/i
const CONTENT_CHUNK_BYTES = 64 * 1024
const INTENT_PREFIX = 'treechat-owner-attachment-write-intent:'
const INTENT_TTL_MS = 30 * 60 * 1000
const encoder = new TextEncoder()
const runtimeIntents = new Map<string, OwnerAttachmentWriteIntent>()

export type OwnerAttachmentWriteManifest = {
    version: 1
    operation: 'owner_attachment_write'
    signingRequestId: string
    attemptId: string
    userId: string
    deviceId: string
    answerId: string
    attachmentId: string
    txVersion: number
    lockTime: number
    paymentAddress: string
    contentBytes: number
    contentSha256: string
    recordSuffixHex: string
    inputs: {
        txid: string
        vout: number
        satoshis: number
        scriptPubKey: string
        sequence: number
        role: 'payment'
    }[]
    outputs: {
        vout: number
        satoshis: number
        scriptBytes: number
        scriptSha256: string
        script?: string
    }[]
    recordVout: 0
    networkFeeSatoshis: number
    changeSatoshis: number
}

/** What the owner confirmed on this device when they tapped Write. */
export type OwnerAttachmentWriteIntent = {
    version: 1
    userId: string
    deviceId: string
    answerId: string
    /** The attachment the Write was for, when the app knew it (else the post's default). */
    attachmentId?: string
    maxNetworkFeeSatoshis: number
    createdAt: number
}

export type OwnerAttachmentWriteSignature = {
    manifestDigest: string
    inputIndex: number
    txid: string
    vout: number
    unlockingScriptHex: string
}

function validIntent(intent: OwnerAttachmentWriteIntent): boolean {
    return (
        intent?.version === 1 &&
        Boolean(intent.userId && intent.deviceId && intent.answerId) &&
        Number.isSafeInteger(intent.maxNetworkFeeSatoshis) &&
        intent.maxNetworkFeeSatoshis > 0 &&
        Number.isSafeInteger(intent.createdAt) &&
        intent.createdAt <= Date.now() &&
        Date.now() - intent.createdAt <= INTENT_TTL_MS
    )
}

// One authorization per attachment, so two writes on one post don't replace each
// other; a Write whose attachment the app didn't name is kept under the post alone.
function intentKey(answerId: string, attachmentId?: string): string {
    return attachmentId ? `${answerId}:${attachmentId}` : answerId
}

export function saveOwnerAttachmentWriteIntent(intent: OwnerAttachmentWriteIntent): void {
    if (!validIntent(intent)) throw new Error('Invalid onchain write authorization')
    const key = intentKey(intent.answerId, intent.attachmentId)
    runtimeIntents.set(key, intent)
    try {
        localStorage.setItem(`${INTENT_PREFIX}${key}`, JSON.stringify(intent))
    } catch {
        // Keep the current page able to sign when persistent storage is restricted.
    }
}

function readIntent(key: string, answerId: string): OwnerAttachmentWriteIntent | null {
    const runtime = runtimeIntents.get(key)
    if (runtime && validIntent(runtime)) return runtime
    try {
        const serialized = localStorage.getItem(`${INTENT_PREFIX}${key}`)
        if (!serialized) return null
        const intent = JSON.parse(serialized) as OwnerAttachmentWriteIntent
        if (validIntent(intent) && intent.answerId === answerId) return intent
    } catch {
        return null
    }
    runtimeIntents.delete(key)
    try {
        localStorage.removeItem(`${INTENT_PREFIX}${key}`)
    } catch {}
    return null
}

function intentKeysForPost(answerId: string): string[] {
    const keys = new Set<string>()
    for (const key of runtimeIntents.keys()) if (key.startsWith(`${answerId}:`)) keys.add(key)
    try {
        for (let i = 0; i < localStorage.length; i += 1) {
            const stored = localStorage.key(i)
            if (stored?.startsWith(`${INTENT_PREFIX}${answerId}:`))
                keys.add(stored.slice(INTENT_PREFIX.length))
        }
    } catch {}
    return [...keys]
}

/**
 * The Write authorization for this attachment, else the post's default one, else the
 * post's only one: the server may write a converted copy (a video's MP4) under another
 * attachment id than the one the owner tapped.
 */
export function getOwnerAttachmentWriteIntent(
    answerId: string,
    attachmentId?: string,
): OwnerAttachmentWriteIntent | null {
    const exact = attachmentId && readIntent(intentKey(answerId, attachmentId), answerId)
    if (exact) return exact
    const postDefault = readIntent(intentKey(answerId), answerId)
    if (postDefault) return postDefault
    const others = intentKeysForPost(answerId)
        .map(key => readIntent(key, answerId))
        .filter((intent): intent is OwnerAttachmentWriteIntent => !!intent)
    return others.length === 1 ? others[0] : null
}

export function clearOwnerAttachmentWriteIntent(answerId: string, attachmentId?: string): void {
    const key = intentKey(answerId, attachmentId)
    runtimeIntents.delete(key)
    try {
        localStorage.removeItem(`${INTENT_PREFIX}${key}`)
    } catch {}
}

/** The same miner-fee ceiling local mints authorize: twice the quoted rate, at least 1,000 sats. */
export function ownerAttachmentWriteMaxNetworkFee(
    byteSize: number,
    feeRateSatsPerByte: number,
): number {
    const rate = Math.max(Number(feeRateSatsPerByte) || 0.1, 0.1)
    return Math.max(1_000, Math.ceil((Number(byteSize) + 2_000) * rate * 2))
}

function bytes(hex: string): Uint8Array {
    if (hex.length % 2 !== 0 || !/^[0-9a-f]*$/i.test(hex)) throw new Error('Invalid hex')
    return Uint8Array.from(hex.match(/../g)?.map(byte => Number.parseInt(byte, 16)) ?? [])
}

function hex(value: ArrayLike<number>): string {
    return Array.from(value, byte => byte.toString(16).padStart(2, '0')).join('')
}

function sha256(value: ArrayLike<number>): Uint8Array {
    return Uint8Array.from(new Hash.SHA256().update(value).digest())
}

function hash256(value: ArrayLike<number>): Uint8Array {
    return sha256(sha256(value))
}

function safeInteger(value: number, minimum = 0): boolean {
    return Number.isSafeInteger(value) && value >= minimum
}

function canonicalize(value: unknown): unknown {
    if (Array.isArray(value)) return value.map(canonicalize)
    if (value && typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value as Record<string, unknown>)
                .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0))
                .map(([key, entry]) => [key, canonicalize(entry)]),
        )
    }
    return value
}

function pushPrefix(length: number): Uint8Array {
    if (!safeInteger(length) || length >= 2 ** 32) throw new Error('Unsupported pushdata length')
    if (length < 76) return Uint8Array.of(length)
    if (length < 256) return Uint8Array.of(76, length)
    if (length < 65536) return Uint8Array.of(77, length & 255, length >>> 8)
    return Uint8Array.of(
        78,
        length & 255,
        (length >>> 8) & 255,
        (length >>> 16) & 255,
        length >>> 24,
    )
}

/** OP_FALSE OP_RETURN <B> <length prefix of the file>; the file bytes follow. */
function recordPrefix(contentBytes: number): Uint8Array {
    const protocol = encoder.encode(B_PROTOCOL_ADDRESS)
    const parts = [Uint8Array.of(0, 106), pushPrefix(protocol.length), protocol]
    parts.push(pushPrefix(contentBytes))
    const prefix = new Uint8Array(parts.reduce((sum, part) => sum + part.length, 0))
    let offset = 0
    for (const part of parts) {
        prefix.set(part, offset)
        offset += part.length
    }
    return prefix
}

/** The record's trailing fields must be plain data pushes. */
function assertPushOnly(script: Uint8Array): void {
    let offset = 0
    while (offset < script.length) {
        const op = script[offset++]
        let length: number
        if (op < 76) length = op // OP_0 is an empty push
        else if (op === 76) length = script[offset++]
        else if (op === 77) {
            length = script[offset] | (script[offset + 1] << 8)
            offset += 2
        } else throw new Error('Onchain write record has an unexpected opcode')
        offset += length
        if (!Number.isFinite(length) || offset > script.length)
            throw new Error('Onchain write record is truncated')
    }
}

function assertManifest(
    manifest: OwnerAttachmentWriteManifest,
    intent: OwnerAttachmentWriteIntent,
    paymentAddress: string,
): void {
    if (
        manifest.version !== 1 ||
        manifest.operation !== 'owner_attachment_write' ||
        !manifest.signingRequestId ||
        !manifest.attemptId ||
        !manifest.attachmentId
    )
        throw new Error('Invalid onchain write signing request')
    if (
        manifest.userId !== intent.userId ||
        manifest.deviceId !== intent.deviceId ||
        manifest.answerId !== intent.answerId
    )
        throw new Error('Onchain write request has a different user, device or post')
    if (manifest.paymentAddress !== paymentAddress)
        throw new Error('Onchain write request has a different wallet')
    if (manifest.txVersion !== 1 || manifest.lockTime !== 0 || manifest.recordVout !== 0)
        throw new Error('Unsupported onchain write transaction layout')
    if (!safeInteger(manifest.contentBytes, 1) || !HEX_32.test(manifest.contentSha256))
        throw new Error('Onchain write content size or hash is invalid')
    if (
        !safeInteger(manifest.networkFeeSatoshis, 1) ||
        manifest.networkFeeSatoshis > intent.maxNetworkFeeSatoshis
    )
        throw new Error('Onchain write miner fee exceeds the locally authorized limit')
    if (!manifest.inputs.length || manifest.outputs.length < 1 || manifest.outputs.length > 2)
        throw new Error('Unsupported onchain write input or output count')

    const suffix = bytes(manifest.recordSuffixHex)
    assertPushOnly(suffix)
    const record = manifest.outputs[0]
    if (
        record.vout !== 0 ||
        record.satoshis !== 0 ||
        record.script ||
        record.scriptBytes !==
            recordPrefix(manifest.contentBytes).length + manifest.contentBytes + suffix.length
    )
        throw new Error('Onchain write record output changed')

    const paymentScript = new P2PKH().lock(paymentAddress).toHex().toLowerCase()
    if (manifest.outputs.length === 2) {
        const change = manifest.outputs[1]
        if (
            change.vout !== 1 ||
            change.script?.toLowerCase() !== paymentScript ||
            !safeInteger(change.satoshis, 1) ||
            change.satoshis !== manifest.changeSatoshis ||
            change.scriptBytes !== paymentScript.length / 2 ||
            hex(sha256(bytes(change.script))) !== change.scriptSha256
        )
            throw new Error('Onchain write change output changed')
    } else if (manifest.changeSatoshis !== 0) {
        throw new Error('Onchain write change amount changed')
    }

    for (const [index, input] of manifest.inputs.entries()) {
        if (
            !HEX_32.test(input.txid) ||
            !safeInteger(input.vout) ||
            !safeInteger(input.satoshis, 1) ||
            input.role !== 'payment' ||
            input.sequence !== 0xffffffff ||
            input.scriptPubKey.toLowerCase() !== paymentScript
        )
            throw new Error(`Invalid onchain write payment input ${index}`)
        if (
            manifest.inputs.findIndex(
                candidate => candidate.txid === input.txid && candidate.vout === input.vout,
            ) !== index
        )
            throw new Error('Duplicate onchain write payment input')
    }
    const inputTotal = manifest.inputs.reduce((sum, input) => sum + input.satoshis, 0)
    const outputTotal = manifest.outputs.reduce((sum, output) => sum + output.satoshis, 0)
    if (
        !safeInteger(inputTotal) ||
        !safeInteger(outputTotal) ||
        inputTotal - outputTotal !== manifest.networkFeeSatoshis
    )
        throw new Error('Onchain write miner fee differs from inputs minus outputs')
}

async function yieldToBrowser(): Promise<void> {
    await new Promise<void>(resolve => {
        if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => resolve())
        else setTimeout(resolve, 0)
    })
}

/** hashOutputs for the write, streaming the record's file bytes from `source`. */
async function hashWriteOutputs(
    source: MintContentSource,
    manifest: OwnerAttachmentWriteManifest,
): Promise<Uint8Array> {
    if (source.size !== manifest.contentBytes) throw new Error('Onchain write content size changed')
    const contentHash = new Hash.SHA256()
    const scriptHash = new Hash.SHA256()
    const outputsHash = new Hash.SHA256()
    const record = manifest.outputs[0]
    const header = new Utils.Writer()
    header.writeUInt64LE(record.satoshis)
    header.writeVarIntNum(record.scriptBytes)
    outputsHash.update(Array.from(header.toUint8Array()))
    const feedScript = (chunk: Uint8Array) => {
        scriptHash.update(chunk as unknown as number[])
        outputsHash.update(chunk as unknown as number[])
    }
    feedScript(recordPrefix(manifest.contentBytes))
    let chunkIndex = 0
    for (let offset = 0; offset < source.size; offset += CONTENT_CHUNK_BYTES) {
        const end = Math.min(offset + CONTENT_CHUNK_BYTES, source.size)
        const chunk = await source.readChunk(offset, end)
        if (chunk.length !== end - offset) throw new Error('Onchain write content is incomplete')
        contentHash.update(chunk as unknown as number[])
        feedScript(chunk)
        // Keep WebViews responsive while a large file streams through the hashes.
        if (++chunkIndex % 4 === 0) await yieldToBrowser()
    }
    if (hex(contentHash.digest()) !== manifest.contentSha256.toLowerCase())
        throw new Error('Onchain write content does not match the request')
    feedScript(bytes(manifest.recordSuffixHex))
    if (hex(scriptHash.digest()) !== record.scriptSha256.toLowerCase())
        throw new Error('Onchain write record does not match the request')
    for (const output of manifest.outputs.slice(1)) {
        const script = bytes(output.script!)
        const outputHeader = new Utils.Writer()
        outputHeader.writeUInt64LE(output.satoshis)
        outputHeader.writeVarIntNum(script.length)
        outputsHash.update(Array.from(outputHeader.toUint8Array()))
        outputsHash.update(Array.from(script))
    }
    return sha256(outputsHash.digest())
}

export async function signOwnerAttachmentWrite(
    manifest: OwnerAttachmentWriteManifest,
    intent: OwnerAttachmentWriteIntent,
    content: MintContentSource,
    paymentKey: PrivateKey | string,
): Promise<OwnerAttachmentWriteSignature[]> {
    if (!validIntent(intent)) throw new Error('This device has no current onchain write to sign')
    const key = typeof paymentKey === 'string' ? PrivateKey.fromWif(paymentKey) : paymentKey
    const paymentAddress = key.toAddress()
    assertManifest(manifest, intent, paymentAddress)
    const hashOutputs = await hashWriteOutputs(content, manifest)
    const prevouts = new Utils.Writer()
    const sequences = new Utils.Writer()
    for (const input of manifest.inputs) {
        prevouts.writeReverse(Array.from(bytes(input.txid)))
        prevouts.writeUInt32LE(input.vout)
        sequences.writeUInt32LE(input.sequence)
    }
    const hashPrevouts = hash256(prevouts.toUint8Array())
    const hashSequence = hash256(sequences.toUint8Array())
    const paymentScript = bytes(new P2PKH().lock(paymentAddress).toHex())
    const manifestDigest = hex(sha256(encoder.encode(JSON.stringify(canonicalize(manifest)))))
    return manifest.inputs.map((input, inputIndex) => {
        const preimage = new Utils.Writer()
        preimage.writeInt32LE(manifest.txVersion)
        preimage.write(Array.from(hashPrevouts))
        preimage.write(Array.from(hashSequence))
        preimage.writeReverse(Array.from(bytes(input.txid)))
        preimage.writeUInt32LE(input.vout)
        preimage.writeVarIntNum(paymentScript.length)
        preimage.write(Array.from(paymentScript))
        preimage.writeUInt64LE(input.satoshis)
        preimage.writeUInt32LE(input.sequence)
        preimage.write(Array.from(hashOutputs))
        preimage.writeUInt32LE(manifest.lockTime)
        preimage.writeUInt32LE(SIGHASH_ALL_FORKID)
        const rawSignature = key.sign(Array.from(sha256(preimage.toUint8Array())))
        const signature = new TransactionSignature(
            rawSignature.r,
            rawSignature.s,
            SIGHASH_ALL_FORKID,
        )
        const checksig = signature.toChecksigFormat()
        const script = new UnlockingScript([
            { op: checksig.length, data: checksig },
            { op: 33, data: key.toPublicKey().encode(true) as number[] },
        ])
        return {
            manifestDigest,
            inputIndex,
            txid: input.txid,
            vout: input.vout,
            unlockingScriptHex: script.toHex(),
        }
    })
}
